import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Marquee from 'react-fast-marquee';
import { motion } from 'framer-motion';
import { Calendar, Clock, Phone, Mail, ChevronRight, MessageCircle } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Calendar as CalendarUI } from '../components/ui/calendar';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Toaster } from '../components/ui/sonner';
import { toast } from 'sonner';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Facebook SVG icon
function FacebookIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
  );
}

// Instagram SVG icon
function InstagramIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
  );
}

// Hero Section
function HeroSection({ content }) {
  const heroTitle = (content.hero_title || "Elevated\nHair Artistry").split('\n');
  const heroImg = content.hero_image || "https://customer-assets.emergentagent.com/job_beauty-appt-hub-2/artifacts/9lj9v4cn_artistry%20salon.png";
  const heroSrc = heroImg.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${heroImg}` : heroImg;
  return (
    <section className="min-h-screen flex items-center px-6 md:px-12 lg:px-24 pt-20" data-testid="hero-section">
      <div className="grid lg:grid-cols-2 gap-12 items-center w-full max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
        >
          <span className="text-xs tracking-[0.2em] uppercase font-semibold text-[#756A63] mb-4 block">
            {content.hero_subtitle || "Premium Hair Artistry"}
          </span>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-none font-light text-[#2B2623] mb-6">
            {heroTitle.map((line, i) => (
              <span key={i}>{line}{i < heroTitle.length - 1 && <br />}</span>
            ))}
          </h1>
          <p className="text-lg text-[#756A63] mb-8 max-w-md leading-relaxed">
            {content.hero_description || "Experience the art of protective styling with precision, care, and creativity that celebrates your natural beauty."}
          </p>
          <div className="flex flex-wrap gap-4">
            <a href="#booking">
              <Button 
                data-testid="book-appointment-btn"
                className="rounded-full bg-[#9C6A48] hover:bg-[#8B5A33] text-white px-8 py-6 text-base transition-all duration-300 hover:shadow-lg"
              >
                Book Appointment
                <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </a>
            <a href="#services">
              <Button 
                variant="outline"
                className="rounded-full border-[#9C6A48] text-[#9C6A48] hover:bg-[#9C6A48]/5 px-8 py-6 text-base"
              >
                View Services
              </Button>
            </a>
          </div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="relative"
        >
          <div className="rounded-3xl overflow-hidden shadow-2xl">
            <img
              src={heroSrc}
              alt="BraidsbyAbbee"
              className="w-full h-[500px] lg:h-[600px] object-cover"
            />
          </div>
          <a href="#booking" className="absolute -bottom-6 -left-6 bg-white/80 backdrop-blur-md rounded-2xl p-4 shadow-lg cursor-pointer hover:shadow-xl hover:scale-105 transition-all duration-300" data-testid="book-online-badge">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-[#9C6A48] rounded-full flex items-center justify-center">
                <Calendar className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-sm font-semibold text-[#2B2623]">Book Online</p>
                <p className="text-xs text-[#756A63]">Easy scheduling</p>
              </div>
            </div>
          </a>
        </motion.div>
      </div>
    </section>
  );
}

// Auto-scrolling Carousel Banner
function CarouselBanner({ images }) {
  const getImageSrc = (url) => {
    if (!url) return '';
    if (url.startsWith('/api/')) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
    return url;
  };

  if (!images || images.length === 0) return null;

  return (
    <section className="py-8 overflow-hidden" data-testid="carousel-banner">
      <Marquee speed={30} gradient={false} pauseOnHover>
        {[...images, ...images].map((img, idx) => (
          <div key={idx} className="mx-3">
            <div className="rounded-2xl overflow-hidden h-[250px] w-[200px] shadow-md">
              <img
                src={getImageSrc(img.url)}
                alt={img.caption || 'Gallery'}
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        ))}
      </Marquee>
    </section>
  );
}

// Interactive Gallery Grid
function GalleryGrid({ images }) {
  const [selected, setSelected] = useState(null);

  const getImageSrc = (url) => {
    if (!url) return '';
    if (url.startsWith('/api/')) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
    return url;
  };

  if (!images || images.length === 0) return null;
  
  return (
    <section className="py-24 px-6 md:px-12 lg:px-24" data-testid="gallery-section">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="text-xs tracking-[0.2em] uppercase font-semibold text-[#756A63] mb-4 block">
            Our Work
          </span>
          <h2 className="text-4xl sm:text-5xl tracking-tight text-[#2B2623] font-light">
            Gallery
          </h2>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {images.map((img, idx) => (
            <motion.div
              key={img.id || idx}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.05 }}
              className="cursor-pointer group"
              onClick={() => setSelected(img)}
              data-testid={`gallery-photo-${idx}`}
            >
              <div className="rounded-2xl overflow-hidden aspect-[3/4] shadow-md group-hover:shadow-xl transition-all duration-300">
                <img
                  src={getImageSrc(img.url)}
                  alt={img.caption || 'Gallery image'}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
              </div>
              {img.caption && (
                <p className="text-sm text-[#756A63] mt-2 text-center">{img.caption}</p>
              )}
            </motion.div>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {selected && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-6"
          onClick={() => setSelected(null)}
          data-testid="gallery-lightbox"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="relative max-w-3xl max-h-[85vh]"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={getImageSrc(selected.url)}
              alt={selected.caption || 'Gallery image'}
              className="max-w-full max-h-[85vh] object-contain rounded-2xl"
            />
            {selected.caption && (
              <p className="text-white text-center mt-4 text-lg">{selected.caption}</p>
            )}
            <button
              onClick={() => setSelected(null)}
              className="absolute -top-3 -right-3 w-10 h-10 rounded-full bg-white text-[#2B2623] flex items-center justify-center shadow-lg hover:scale-110 transition-transform text-xl font-light"
              data-testid="close-lightbox-btn"
            >
              &times;
            </button>
          </motion.div>
        </div>
      )}
    </section>
  );
}

// Services Section
function ServicesSection({ services }) {
  const groupedServices = services.reduce((acc, service) => {
    const cat = service.category || 'Other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(service);
    return acc;
  }, {});

  return (
    <section id="services" className="py-24 px-6 md:px-12 lg:px-24" data-testid="services-section">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-xs tracking-[0.2em] uppercase font-semibold text-[#756A63] mb-4 block">
            Our Offerings
          </span>
          <h2 className="text-4xl sm:text-5xl tracking-tight text-[#2B2623] font-light">
            Services & Pricing
          </h2>
        </motion.div>

        {Object.entries(groupedServices).map(([category, categoryServices], catIdx) => (
          <div key={category} className="mb-12">
            <h3 className="text-2xl text-[#9C6A48] mb-6 font-light">{category}</h3>
            <div className="space-y-4">
              {categoryServices.map((service, idx) => (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.05 }}
                  className="flex justify-between items-start py-4 border-b border-[#E3DACD] service-card"
                  data-testid={`service-item-${service.id}`}
                >
                  <div className="flex-1">
                    <h4 className="text-lg font-medium text-[#2B2623]">{service.name}</h4>
                    <p className="text-sm text-[#756A63] mt-1">{service.description}</p>
                    <div className="flex items-center gap-2 mt-2 text-xs text-[#756A63]">
                      <Clock className="w-3 h-3" />
                      <span>{service.duration} mins</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-2xl font-light text-[#9C6A48]">${service.price} <span className="text-sm">CAD</span></span>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// About Section
function AboutSection({ content }) {
  return (
    <section id="about" className="py-24 px-6 md:px-12 lg:px-24 bg-[#EDE7DD]" data-testid="about-section">
      <div className="max-w-6xl mx-auto">
        <div className="grid lg:grid-cols-5 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="lg:col-span-2"
          >
            <div className="rounded-3xl overflow-hidden shadow-xl">
              <img
                src={content.bio_image || "https://customer-assets.emergentagent.com/job_beauty-appt-hub-2/artifacts/9lj9v4cn_artistry%20salon.png"}
                alt={content.bio_name || "Abigail Ansah"}
                className="w-full h-[500px] object-cover"
              />
            </div>
            {content.bio_name && (
              <div className="mt-4 text-center">
                <h3 className="text-2xl text-[#2B2623] font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  {content.bio_name}
                </h3>
                <p className="text-sm text-[#756A63] mt-1">Owner & Lead Stylist</p>
              </div>
            )}
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="lg:col-span-3"
          >
            <span className="text-xs tracking-[0.2em] uppercase font-semibold text-[#756A63] mb-4 block">
              My Philosophy
            </span>
            <h2 className="text-4xl sm:text-5xl tracking-tight text-[#2B2623] font-light mb-6">
              {content.bio_title || "Meet Abby"}
            </h2>
            <p className="text-lg text-[#756A63] leading-relaxed">
              {content.bio_text || "With over 10 years of experience in protective styling, I'm passionate about creating beautiful, healthy hairstyles that celebrate your natural beauty. Every braid tells a story, and I'm here to help you tell yours."}
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a 
                href={`https://wa.me/${(content.whatsapp_number || '905-965-4909').replace(/\D/g, '')}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button className="rounded-full bg-[#25D366] hover:bg-[#128C7E] text-white px-6">
                  <MessageCircle className="mr-2 h-4 w-4" />
                  WhatsApp Me
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// Booking Section
function BookingSection({ services, content }) {
  const [date, setDate] = useState(null);
  const [selectedService, setSelectedService] = useState('');
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    time: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [blockedDates, setBlockedDates] = useState([]);
  const [schedule, setSchedule] = useState(null);

  useEffect(() => {
    fetchSchedule();
  }, []);

  const fetchSchedule = async () => {
    try {
      const { data } = await axios.get(`${API}/schedule`);
      setSchedule(data.weekly_hours);
      setBlockedDates(data.blocked_dates || []);
    } catch (e) {
      console.error('Failed to fetch schedule');
    }
  };

  useEffect(() => {
    if (date) {
      fetchAvailableSlots(format(date, 'yyyy-MM-dd'));
    }
  }, [date]);

  const fetchAvailableSlots = async (dateStr) => {
    setSlotsLoading(true);
    try {
      const { data } = await axios.get(`${API}/schedule/available-slots?date=${dateStr}`);
      setAvailableSlots(data.slots || []);
      if (data.slots?.length === 0) {
        toast.info(data.message || 'No slots available on this date');
      }
    } catch (e) {
      setAvailableSlots([]);
    } finally {
      setSlotsLoading(false);
    }
  };

  const isDateDisabled = (d) => {
    if (d < new Date()) return true;
    const dateStr = format(d, 'yyyy-MM-dd');
    if (blockedDates.includes(dateStr)) return true;
    if (schedule) {
      const dayName = d.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
      const dayConfig = schedule[dayName];
      if (dayConfig && !dayConfig.enabled) return true;
    }
    return false;
  };

  const depositAmount = content.deposit_amount || 30;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!date || !selectedService || !formData.time) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/appointments`, {
        ...formData,
        service_id: selectedService,
        date: format(date, 'yyyy-MM-dd'),
      });
      toast.success('Appointment booked! Check your email for confirmation.');
      setFormData({ customer_name: '', customer_email: '', customer_phone: '', time: '', notes: '' });
      setDate(null);
      setSelectedService('');
      setAvailableSlots([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to book appointment');
    } finally {
      setLoading(false);
    }
  };

  const bookingPolicy = content.booking_policy || [
    `Appointments require a $${depositAmount.toFixed(2)} CAD deposit to confirm`,
    "24-hour cancellation notice required",
    "Late arrivals may result in shortened service",
    "Please arrive with clean, detangled hair"
  ];

  return (
    <section id="booking" className="py-24 px-6 md:px-12 lg:px-24" data-testid="booking-section">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-xs tracking-[0.2em] uppercase font-semibold text-[#756A63] mb-4 block">
            Schedule Your Visit
          </span>
          <h2 className="text-4xl sm:text-5xl tracking-tight text-[#2B2623] font-light">
            Book an Appointment
          </h2>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="text-sm font-medium text-[#2B2623] mb-2 block">Your Name *</label>
                <Input
                  data-testid="booking-name-input"
                  value={formData.customer_name}
                  onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                  placeholder="Enter your name"
                  required
                  className="h-12 rounded-xl border-[#E3DACD] bg-white/50 focus-visible:ring-[#9C6A48]"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-[#2B2623] mb-2 block">Email *</label>
                <Input
                  data-testid="booking-email-input"
                  type="email"
                  value={formData.customer_email}
                  onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                  placeholder="your@email.com"
                  required
                  className="h-12 rounded-xl border-[#E3DACD] bg-white/50 focus-visible:ring-[#9C6A48]"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-[#2B2623] mb-2 block">Phone *</label>
                <Input
                  data-testid="booking-phone-input"
                  type="tel"
                  value={formData.customer_phone}
                  onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                  placeholder="Your phone number"
                  required
                  className="h-12 rounded-xl border-[#E3DACD] bg-white/50 focus-visible:ring-[#9C6A48]"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-[#2B2623] mb-2 block">Service *</label>
                <Select value={selectedService} onValueChange={setSelectedService}>
                  <SelectTrigger data-testid="booking-service-select" className="h-12 rounded-xl border-[#E3DACD] bg-white/50">
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent>
                    {services.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name} - ${s.price} CAD
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-[#2B2623] mb-2 block">Select Date *</label>
                <div className="bg-white rounded-xl p-4 border border-[#E3DACD]">
                  <CalendarUI
                    data-testid="booking-calendar"
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    disabled={isDateDisabled}
                    className="rounded-md"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-[#2B2623] mb-2 block">Select Time *</label>
                {slotsLoading ? (
                  <div className="flex items-center gap-2 text-sm text-[#756A63] py-3">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#9C6A48]"></div>
                    Loading available times...
                  </div>
                ) : !date ? (
                  <p className="text-sm text-[#756A63] py-3">Select a date first</p>
                ) : availableSlots.length === 0 ? (
                  <p className="text-sm text-red-500 py-3">No available times on this date</p>
                ) : (
                  <Select value={formData.time} onValueChange={(v) => setFormData({ ...formData, time: v })}>
                    <SelectTrigger data-testid="booking-time-select" className="h-12 rounded-xl border-[#E3DACD] bg-white/50">
                      <SelectValue placeholder="Select a time" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableSlots.map((t) => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
              <div>
                <label className="text-sm font-medium text-[#2B2623] mb-2 block">Notes (optional)</label>
                <Textarea
                  data-testid="booking-notes-input"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Any special requests or notes"
                  className="rounded-xl border-[#E3DACD] bg-white/50 focus-visible:ring-[#9C6A48]"
                />
              </div>
              <Button
                data-testid="submit-booking-btn"
                type="submit"
                disabled={loading}
                className="w-full rounded-full bg-[#9C6A48] hover:bg-[#8B5A33] text-white py-6 text-base"
              >
                {loading ? 'Booking...' : 'Book Appointment'}
              </Button>
            </form>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            <div className="bg-[#FEF3C7] rounded-2xl p-6 border border-[#FCD34D]" data-testid="payment-info">
              <h3 className="text-xl font-medium text-[#92400E] mb-4">Payment Information</h3>
              <p className="text-[#92400E] mb-4">
                A <strong>${depositAmount.toFixed(2)} CAD deposit</strong> is required to confirm your appointment. Please send via Interac e-Transfer:
              </p>
              <div className="bg-white/50 rounded-xl p-4 space-y-2">
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-[#92400E]" />
                  <span className="font-medium text-[#92400E]">{content.payment_email || 'aeansah@gmail.com'}</span>
                </div>
                <p className="text-sm text-[#B45309]">
                  Include your name and appointment date in the message.
                </p>
              </div>
              <p className="text-sm text-[#92400E] mt-4">
                Your appointment will be confirmed once the ${depositAmount.toFixed(2)} CAD deposit is received.
              </p>
            </div>

            <div className="bg-white/60 backdrop-blur-md rounded-2xl p-6 border border-white/20 shadow-sm">
              <h3 className="text-xl font-medium text-[#2B2623] mb-4">Contact Us</h3>
              <div className="space-y-4">
                <a 
                  href={`https://wa.me/${(content.whatsapp_number || '905-965-4909').replace(/\D/g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-[#25D366] hover:underline"
                >
                  <MessageCircle className="w-5 h-5" />
                  <span>WhatsApp: {content.whatsapp_number || '905-965-4909'}</span>
                </a>
                <div className="flex items-center gap-3 text-[#756A63]">
                  <Mail className="w-5 h-5" />
                  <span>{content.payment_email || 'aeansah@gmail.com'}</span>
                </div>
              </div>
            </div>

            <div className="bg-[#FAF7F2] rounded-2xl p-6 border border-[#E3DACD]">
              <h3 className="text-xl font-medium text-[#2B2623] mb-4">Booking Policy</h3>
              <ul className="space-y-2 text-sm text-[#756A63]">
                {bookingPolicy.map((item, i) => (
                  <li key={i}>&#8226; {item}</li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// WhatsApp Button
function WhatsAppButton({ number }) {
  const formattedNumber = (number || '905-965-4909').replace(/\D/g, '');
  
  return (
    <a
      href={`https://wa.me/${formattedNumber}`}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="whatsapp-button"
      className="fixed bottom-6 right-6 z-50 rounded-full h-14 w-14 shadow-lg bg-[#25D366] text-white flex items-center justify-center hover:scale-110 transition-transform whatsapp-pulse"
    >
      <MessageCircle className="w-6 h-6" />
    </a>
  );
}

// Header
function Header({ content }) {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
        scrolled ? 'backdrop-blur-xl bg-[#FAF7F2]/70 border-b border-[#E3DACD]/50 shadow-sm' : ''
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
        <a href="#" className="text-2xl font-light tracking-tight text-[#2B2623]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
          BraidsbyAbbee
        </a>
        <nav className="hidden md:flex items-center gap-8">
          <a href="#services" className="text-sm text-[#756A63] hover:text-[#9C6A48] transition-colors">Services</a>
          <a href="#about" className="text-sm text-[#756A63] hover:text-[#9C6A48] transition-colors">About</a>
          <a href="#booking" className="text-sm text-[#756A63] hover:text-[#9C6A48] transition-colors">Book</a>
          {content.facebook_url && (
            <a href={content.facebook_url} target="_blank" rel="noopener noreferrer" className="text-[#756A63] hover:text-[#1877F2] transition-colors" data-testid="header-facebook">
              <FacebookIcon className="w-4 h-4" />
            </a>
          )}
          {content.instagram_url && (
            <a href={content.instagram_url} target="_blank" rel="noopener noreferrer" className="text-[#756A63] hover:text-[#E1306C] transition-colors" data-testid="header-instagram">
              <InstagramIcon className="w-4 h-4" />
            </a>
          )}
          <Link to="/admin" className="text-sm text-[#756A63] hover:text-[#9C6A48] transition-colors">Dashboard</Link>
        </nav>
        <a href="#booking">
          <Button className="rounded-full bg-[#9C6A48] hover:bg-[#8B5A33] text-white px-6 hidden md:flex">
            Book Now
          </Button>
        </a>
      </div>
    </header>
  );
}

// Footer
function Footer({ content }) {
  return (
    <footer className="py-12 px-6 md:px-12 lg:px-24 bg-[#2B2623] text-white">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-center gap-6">
          <div>
            <h3 className="text-2xl font-light tracking-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              BraidsbyAbbee
            </h3>
            <p className="text-sm text-white/60 mt-1">Elevated Hair Artistry</p>
          </div>
          <div className="flex gap-6 text-sm text-white/60">
            <a href="#services" className="hover:text-white transition-colors">Services</a>
            <a href="#about" className="hover:text-white transition-colors">About</a>
            <a href="#booking" className="hover:text-white transition-colors">Book</a>
          </div>
          <div className="flex items-center gap-4">
            {content.facebook_url && (
              <a href={content.facebook_url} target="_blank" rel="noopener noreferrer" data-testid="facebook-link" className="text-white/60 hover:text-white transition-colors">
                <FacebookIcon className="w-5 h-5" />
              </a>
            )}
            {content.instagram_url && (
              <a href={content.instagram_url} target="_blank" rel="noopener noreferrer" data-testid="instagram-link" className="text-white/60 hover:text-white transition-colors">
                <InstagramIcon className="w-5 h-5" />
              </a>
            )}
            <a
              href={`https://wa.me/${(content.whatsapp_number || '905-965-4909').replace(/\D/g, '')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/60 hover:text-white transition-colors"
            >
              <MessageCircle className="w-5 h-5" />
            </a>
          </div>
        </div>
        <div className="border-t border-white/10 mt-8 pt-8 text-center text-sm text-white/40">
          &copy; {new Date().getFullYear()} BraidsbyAbbee. All rights reserved.
        </div>
      </div>
    </footer>
  );
}

// Main Landing Page Component
export default function LandingPage() {
  const [services, setServices] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [content, setContent] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [servicesRes, galleryRes, contentRes] = await Promise.all([
        axios.get(`${API}/services`),
        axios.get(`${API}/gallery`),
        axios.get(`${API}/content`),
      ]);
      setServices(servicesRes.data);
      setGallery(galleryRes.data);
      setContent(contentRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF7F2]">
      <Toaster position="top-center" richColors />
      <Header content={content} />
      <HeroSection content={content} />
      <CarouselBanner images={gallery} />
      <ServicesSection services={services} />
      <AboutSection content={content} />
      <GalleryGrid images={gallery} />
      <BookingSection services={services} content={content} />
      <Footer content={content} />
      <WhatsAppButton number={content.whatsapp_number} />
    </div>
  );
}
